import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
    TextField,
    Autocomplete,
    Typography,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from 'hooks';
import { Button as LoadingButton } from '@mui/material';

const useStyles = makeStyles()(({ spacing, transitions }) => ({
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
    },
    closeBtn: {
        width: 48,
        height: 48,
        margin: spacing(1),
    },
    section: {
        margin: `${spacing(1)} ${spacing(0)}`,
    },
    toggle: {
        padding: `${spacing(0.5)} ${spacing(2)}`,
    },
    transitionWidth: {
        minWidth: 232,
        transition: `${transitions.create('width', {
            easing: transitions.easing.sharp,
            duration: transitions.duration.standard,
        })}`,
    },
}));

interface Props {
    open: boolean;
    onClose: (reload: boolean) => void;
    onSavePlanDetails: (
        bfmcId: number,
        planId: string,
        newPlanCode: string,
        newPlanName: string,
    ) => void;
}

/**
 * Is a Dialogue box used to add a new Plan.
 */
const UpdatePlanDialog: React.FunctionComponent<Props> = ({
    open,
    onClose,
    onSavePlanDetails,
}) => {
    const { classes } = useStyles();

    const { selectedPlan, selectedBFMC, selectedPlanType, updatePlanResponse } =
        useAppSelector((state) => state.guide);

    const bfmcId = selectedBFMC?.id ?? -1;
    const planId = selectedPlan?.id ?? '';
    const originalPlanCode = selectedPlan?.code ?? '';
    const originalPlanName = selectedPlan?.name ?? '';
    const bfmcName = selectedBFMC?.name ?? '';

    const planPrefix = originalPlanCode.slice(0, 2);
    const year = parseInt(originalPlanCode.slice(2), 10);
    const currentPlanYear = !isNaN(year) ? year : null;
    const currentYear = new Date().getFullYear();

    const yearOptions = React.useMemo(
        () => Array.from({ length: 5 }, (_, i) => currentYear - 3 + i),
        [currentYear],
    );

    const [planYear, setPlanYear] = React.useState(currentPlanYear);
    const [planName, setPlanName] = React.useState('');
    const [derivedPlanName, setDerivedPlanName] = React.useState('');
    const [planCode, setPlanCode] = React.useState('');
    const [hasChanged, setHasChanged] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    const isValidPlanName = (name: string) =>
        /^[a-zA-Z0-9_/-]+(?: [a-zA-Z0-9_/-]+)*$/.test(name.trim());
    const planNameError = !planName
        ? 'Required'
        : !isValidPlanName(planName)
          ? 'Only letters, numbers, spaces, /, -, and _ are allowed'
          : '';

    const isSelectedPlanValid = planId.trim() !== '' && bfmcId !== -1;

    const getPlanName = (
        originalName: string,
        inPlanYear: number,
        inPlanType: string,
    ): string => {
        // Remove suffix like: _2024_01, 2024 01, _2024 01, or just _2024 / 2024
        const suffixRegex = new RegExp(`[ _]?${inPlanYear}(?:[ _]\\d+)?$`);
        let cleanedName = originalName.replace(suffixRegex, '');

        // Remove the planType, case-insensitively
        const typeRegex = new RegExp(inPlanType, 'i');
        cleanedName = cleanedName.replace(typeRegex, '');

        // Remove trailing underscore, duplicate spaces and trim
        cleanedName = cleanedName
            .replace(/_+$/, '')
            .replace(/\s+/g, ' ')
            .trim();
        return cleanedName;
    };

    useEffect(() => {
        setErrorMessage('');
        setIsProcessing(false);
        if (!isNaN(year) && open && originalPlanName) {
            setPlanYear(year);
            setPlanName(getPlanName(originalPlanName, year, selectedPlanType));
        }
    }, [selectedPlan, selectedPlanType, open]);

    useEffect(() => {
        const selectedTypeUpper = selectedPlanType?.toUpperCase() ?? '';
        const rawName = `${planName ?? ''} ${selectedTypeUpper} ${
            planYear ?? ''
        }`;
        const newDerivedName = rawName.replace(/\s+/g, ' ').trim();

        setDerivedPlanName(newDerivedName);
        setPlanCode(`${planPrefix ?? ''}${planYear ?? ''}`);

        const changed =
            planYear !== currentPlanYear || newDerivedName !== originalPlanName;

        setHasChanged(changed);
    }, [planYear, planName, selectedPlanType, bfmcName, currentPlanYear, open]);

    const handleSaveDetails = async () => {
        setIsProcessing(true);
        await onSavePlanDetails(bfmcId, planId, planCode, derivedPlanName);
        setIsProcessing(false);
        if (updatePlanResponse.status == 'error' && updatePlanResponse.error) {
            setErrorMessage(
                updatePlanResponse.error?.data?.detail?.msg ||
                    'Failed to update the plan details',
            );
        } else {
            onClose(true);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)}>
            <div className={classes.header}>
                <DialogTitle>Update Plan Details</DialogTitle>
                <IconButton
                    className={classes.closeBtn}
                    onClick={() => onClose(false)}
                >
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent>
                <DialogContentText>
                    <Autocomplete
                        disablePortal
                        size="small"
                        options={yearOptions}
                        getOptionLabel={(option) => option.toString()}
                        sx={{ width: '100%', mr: 1 }}
                        value={planYear}
                        clearIcon={null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={planYear == null || !planYear}
                                helperText={!planYear ? 'Required' : ''}
                                label="Plan Year"
                            />
                        )}
                        onChange={(e, value) =>
                            setPlanYear(value ?? yearOptions[0])
                        }
                    />

                    <TextField
                        size="small"
                        label="Plan Name"
                        variant="outlined"
                        fullWidth
                        error={!!planNameError}
                        helperText={planNameError}
                        margin="normal"
                        value={planName}
                        onChange={(e) => setPlanName(e.target.value)}
                    />
                    {planName && (
                        <Typography
                            variant="caption"
                            color="#518a3a"
                            sx={{ mt: 0, pt: 0 }}
                        >
                            <b>Derived Plan Name:</b> {derivedPlanName}
                        </Typography>
                    )}
                    <TextField
                        size="small"
                        label="Plan Code"
                        variant="outlined"
                        fullWidth
                        disabled
                        margin="normal"
                        value={planCode}
                    />
                    {errorMessage ? (
                        <Alert
                            variant="standard"
                            sx={{ pl: 1, m: 0 }}
                            severity="error"
                        >
                            {errorMessage}
                        </Alert>
                    ) : (
                        ''
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} color="primary">
                    Cancel
                </Button>
                <LoadingButton
                    className={classes.transitionWidth}
                    variant="contained"
                    onClick={() => handleSaveDetails()}
                    disabled={
                        !hasChanged || !!planNameError || !isSelectedPlanValid
                    }
                    loading={isProcessing}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

UpdatePlanDialog.displayName = 'UpdatePlanDialog';
export default UpdatePlanDialog;
